import { useEffect, useMemo, useState } from "react";
import { useRequest } from "../hooks/useRequest";
import { getCurrentSubscription, getPlans, Plan } from "../api/subscription";
import UpgradeModal from "../payment/UpgradeModal";
import DowngradeModal from "../payment/DowngradeModal";
import ContactModal from "../components/ContactModal";
import "./PlanManagement.scss";

/** 仅用于 UI 的主推套餐标记（接口未提供该字段） */
const POPULAR_PLAN_CODES = new Set(["DEVELOPER"]);

type CardAction = "current" | "switch" | "contact";

interface PlanCardModel {
  planCode: string;
  name: string;
  price: string;
  features: string[];
  popular: boolean;
  action: CardAction;
}

/**
 * 企业版属于「定制洽谈」层，订阅接口的 plans 里不包含它，
 * 作为静态卡片追加展示（价格自定义、引导联系销售）。
 */
const ENTERPRISE_CARD: PlanCardModel = {
  planCode: "ENTERPRISE",
  name: "Enterprise Plan",
  price: "Custom pricing",
  features: [
    "Custom order & API call volume",
    "Volume discounts",
    "Dedicated account manager",
    "Priority support",
    "Custom integrations & onboarding",
  ],
  popular: false,
  action: "contact",
};

/** 月费展示：0 → $0 / month，否则 $X / month */
function formatPrice(feeCents: number): string {
  if (feeCents <= 0) return "$0 / month";
  const dollars = feeCents / 100;
  return `$${dollars.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })} / month`;
}

/**
 * 价格 JSON-LD（schema.org SoftwareApplication + Offer）。
 * 让爬虫理解套餐价格：Free plan 与 Developer plan 各一个 Offer。
 * price 由接口的 monthlyFeeCents 派生（美分 → 两位小数美元字符串）。
 */
function buildPricingJsonLd(plans: Plan[]): string {
  const offers = plans.map((p) => ({
    "@type": "Offer",
    name: `${p.planName} plan`,
    price: (p.monthlyFeeCents / 100).toFixed(2),
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  }));
  const data = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "CloudStore API",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Catalog and Checkout APIs for 6,000+ fashion brands and 400+ US & EU merchants.",
    offers,
    provider: {
      "@type": "Organization",
      name: "CloudStore AI",
      url: "https://www.nubestore.ai/",
    },
  };
  return JSON.stringify(data);
}

/** 美分 → 美元字符串，去掉多余的 0（200 → "$2"，5 → "$0.05"） */
function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}`;
}

/** 从 apiPricing 中取指定 apiCode 的单价（美分） */
function apiPriceCents(plan: Plan, apiCode: string): number | undefined {
  return plan.apiPricing?.find((p) => p.apiCode === apiCode)?.priceCents;
}

/** 各套餐写死的附加文案（接口未提供营销文案） */
const PLAN_EXTRA_FEATURES: Record<string, string[]> = {
  FREE: ["Email support"],
  DEVELOPER: [
    "Third-party platform costs with no markup",
    "Includes 24/7 email support",
    "Free shipping tracking included",
  ],
};

/** 从接口的结构化字段派生要展示的特性列表 */
function planFeatures(plan: Plan): string[] {
  const list: string[] = [
    `${dollars(plan.monthlyCreditsCents)} per month free credits`,
  ];
  const orderCents = apiPriceCents(plan, "checkout.order");
  if (orderCents != null) list.push(`${dollars(orderCents)} per order placed`);
  const fetchCents = apiPriceCents(plan, "product.detail");
  if (fetchCents != null) list.push(`${dollars(fetchCents)} per product fetch`);
  list.push(...(PLAN_EXTRA_FEATURES[plan.planCode] ?? []));
  return list;
}

const PlanManagement = () => {
  useEffect(() => {
    document.title = "Plan Management | CloudStore API";
  }, []);

  const current = useRequest(getCurrentSubscription);
  const plans = useRequest(getPlans);

  const currentPlanCode = current.data?.planCode;
  const currentPlan = plans.data?.find((p) => p.planCode === currentPlanCode);

  // 切换套餐弹窗状态
  const [switchTarget, setSwitchTarget] = useState<Plan | null>(null);
  const [switchMode, setSwitchMode] = useState<"upgrade" | "downgrade" | null>(
    null
  );

  // Contact Us 弹窗
  const [contactOpen, setContactOpen] = useState(false);

  const openSwitch = (planCode: string) => {
    const target = plans.data?.find((p) => p.planCode === planCode);
    if (!target) return;
    const currentFee =
      currentPlan?.monthlyFeeCents ?? current.data?.monthlyFeeCents ?? 0;
    // 目标月费更高 → 升级；更低 → 降级
    setSwitchMode(target.monthlyFeeCents > currentFee ? "upgrade" : "downgrade");
    setSwitchTarget(target);
  };

  const closeSwitch = () => {
    setSwitchTarget(null);
    setSwitchMode(null);
  };

  const handleSwitched = () => {
    current.reload();
    plans.reload();
  };

  // 组合：接口套餐 + 静态企业版
  const cards: PlanCardModel[] = useMemo(() => {
    const apiCards: PlanCardModel[] = (plans.data || []).map((plan) => ({
      planCode: plan.planCode,
      name: plan.planName,
      price: formatPrice(plan.monthlyFeeCents),
      features: planFeatures(plan),
      popular: POPULAR_PLAN_CODES.has(plan.planCode),
      action:
        plan.planCode === currentPlanCode
          ? ("current" as const)
          : ("switch" as const),
    }));
    return [...apiCards, ENTERPRISE_CARD];
  }, [plans.data, currentPlanCode]);

  const renderButton = (card: PlanCardModel) => {
    switch (card.action) {
      case "current":
        return (
          <button
            type="button"
            className="cs-plan-card__btn cs-plan-card__btn--current"
            disabled
          >
            Current Plan
          </button>
        );
      case "contact":
        return (
          <button
            type="button"
            className="cs-plan-card__btn cs-plan-card__btn--contact"
            onClick={() => setContactOpen(true)}
          >
            Contact Sales
          </button>
        );
      default:
        return (
          <button
            type="button"
            className="cs-plan-card__btn cs-plan-card__btn--switch"
            onClick={() => openSwitch(card.planCode)}
          >
            Switch to this plan
          </button>
        );
    }
  };

  const loading = current.loading || plans.loading;
  const error = current.error || plans.error;

  return (
    <div className="cs-plans">
      {/* JSON-LD：向爬虫声明套餐价格（Free plan / Developer plan 各一个 Offer） */}
      {(plans.data || []).length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: buildPricingJsonLd(plans.data || []),
          }}
        />
      )}
      <h1 className="cs-plans__title">Plan Management</h1>

      {loading && <div className="cs-plans__muted">Loading…</div>}
      {!loading && error && <div className="cs-plans__error">{error}</div>}

      {!loading && !error && (
        <div className="cs-plans__grid">
          {cards.map((card) => (
            <div
              key={card.planCode}
              className={`cs-plan-card${
                card.popular ? " cs-plan-card--popular" : ""
              }`}
            >
              {card.popular && (
                <span className="cs-plan-card__badge">Most Popular</span>
              )}
              <div className="cs-plan-card__name">{card.name}</div>
              <div className="cs-plan-card__price">{card.price}</div>
              <ul className="cs-plan-card__features">
                {card.features.map((f, i) => (
                  <li key={i} className="cs-plan-card__feature">
                    <span className="cs-plan-card__check">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {renderButton(card)}
            </div>
          ))}
        </div>
      )}

      {/* 升级弹窗（流程三） */}
      <UpgradeModal
        open={switchMode === "upgrade"}
        onClose={closeSwitch}
        currentPlan={currentPlan}
        targetPlan={switchTarget}
        onSuccess={handleSwitched}
      />

      {/* 降级弹窗（流程四） */}
      <DowngradeModal
        open={switchMode === "downgrade"}
        onClose={closeSwitch}
        currentPlan={currentPlan}
        targetPlan={switchTarget}
        currentSub={current.data}
        onSuccess={handleSwitched}
      />

      {/* Contact Us 弹窗 */}
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
};

export default PlanManagement;
